import { InputSelect, Select } from '@lmc/ui-kit'
import { Heading, Text } from '@lmc/ui-kit/src/general'
import { Divider } from 'antd'
import { useGroupedCarersWithSelected } from 'hooks/useGroupedOptions'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import type React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { getStatusOptions } from './utils'

interface UpdateActionsModalProps {
  selectedStatus: string
  selectedOwner: string
  onStatusChange: (status: string) => void
  onOwnerChange: (owner: string) => void
  testId?: string
}

const UpdateActionsModal: React.FC<UpdateActionsModalProps> = ({
  selectedStatus,
  selectedOwner,
  onStatusChange,
  onOwnerChange,
  testId = 'update-actions-modal'
}) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const groupedCarerOptions = useGroupedCarersWithSelected({
    assignedUserId: selectedOwner
  })

  const statusOptions = getStatusOptions(t)

  const handleStatusChange = (value: unknown) => {
    onStatusChange(value as string)
  }

  const handleOwnerChange = (value: unknown) => {
    onOwnerChange(value as string)
  }

  return (
    <Container data-testid={testId}>
      <Title level={2} weight="medium" data-testid={`${testId}-title`}>
        {t(incidentReportsConstants.UPDATE_AN_ACTION)}
      </Title>

      <Divider data-testid={`${testId}-divider-top`} />
      <FormContainer data-testid={`${testId}-form-container`}>
        <FormSection data-testid={`${testId}-status-section`}>
          <Label
            level={1}
            weight="medium"
            data-testid={`${testId}-status-label`}
          >
            {t(incidentReportsConstants.UPDATE_STATUS)}:
          </Label>
          <StyledSelect
            data-testid={`${testId}-status-select`}
            placeholder="Select status"
            options={statusOptions}
            value={selectedStatus}
            onChange={handleStatusChange}
          />
        </FormSection>

        <FormSection data-testid={`${testId}-owner-section`}>
          <Label
            level={1}
            weight="medium"
            data-testid={`${testId}-owner-label`}
          >
            {t(incidentReportsConstants.OWNER)}:
          </Label>
          <StyledInputSelect
            testId={`${testId}-owner-select`}
            placeholder={t(incidentReportsConstants.UNASSIGNED)}
            groupedOptions={groupedCarerOptions}
            value={selectedOwner || undefined}
            onChange={handleOwnerChange}
            showSearch={true}
            hasClear={true}
            listHeight={200}
            virtual={true}
          />
        </FormSection>
      </FormContainer>
      <Divider data-testid={`${testId}-divider-bottom`} />
    </Container>
  )
}

const Container = styled.div``

const Title = styled(Heading)`
  padding-bottom: 16px;
  color: ${({ theme }) => theme.color.neutral.color3};
`

const FormSection = styled.div`
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`

const FormContainer = styled.div`
  margin: 40px 0;
`

const Label = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
`

const StyledSelect = styled(Select)`
  width: 332px;

  .ant-select-selector {
    border-color: ${({ theme }) => theme.color.neutral.color5} !important;
  }

  .ant-select-arrow {
    color: ${({ theme }) => theme.color.neutral.color5} !important;
  }
`

const StyledInputSelect = styled(InputSelect)`
  width: 332px !important;
  min-width: 332px !important;

  .ant-select-selector {
    border-color: ${({ theme }) => theme.color.neutral.color5} !important;
  }
`

export default UpdateActionsModal
